import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Plus, Trash2, Edit, ChevronRight, ChevronDown, FolderTree,
  Download, Upload, Eye, EyeOff, Menu, GripVertical, Search
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { API } from "@/lib/api";

export default function AdminCategories() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [dragIdx, setDragIdx] = useState(null);

  const loadCategories = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/categories`)
      .then(res => { setCategories(res.data); setSelected(new Set()); })
      .catch(() => toast.error('Chyba nacteni kategorii'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const deleteCategory = async (catId) => {
    if (!window.confirm('Opravdu smazat kategorii?')) return;
    try {
      await axios.delete(`${API}/admin/categories/${catId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kategorie smazana');
      loadCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba mazani'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Opravdu smazat ${selected.size} kategorii?`)) return;
    setBulkProcessing(true);
    try {
      const res = await axios.post(`${API}/admin/categories/bulk-delete`, { ids: Array.from(selected) }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.errors?.length > 0) toast.error(`Smazano ${res.data.deleted_count}, chyby: ${res.data.errors.join('; ')}`);
      else toast.success(`Smazano ${res.data.deleted_count} kategorii`);
      loadCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba'); }
    finally { setBulkProcessing(false); }
  };

  const toggleActive = async (catId, currentActive) => {
    try {
      await axios.put(`${API}/admin/categories/${catId}`, { active: currentActive ? 0 : 1 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(currentActive ? 'Kategorie deaktivovana' : 'Kategorie aktivovana');
      loadCategories();
    } catch { toast.error('Chyba'); }
  };

  const toggleMenu = async (catId, current) => {
    try {
      await axios.put(`${API}/admin/categories/${catId}`, { show_in_menu: !current }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(!current ? 'Pridano do menu' : 'Odebrano z menu');
      loadCategories();
    } catch { toast.error('Chyba'); }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set();
    const addIds = (cats) => cats.forEach(c => { if (c.children?.length) { all.add(c.id_category); addIds(c.children); } });
    addIds(categories);
    setExpanded(all);
  };

  const flatCategories = (cats, depth = 0) => {
    let flat = [];
    for (const cat of cats) {
      if (search && !cat.name.toLowerCase().includes(search.toLowerCase())) {
        if (cat.children?.length) {
          const childResults = flatCategories(cat.children, depth + 1);
          if (childResults.length > 0) {
            flat.push({ ...cat, depth });
            flat.push(...childResults);
          }
        }
        continue;
      }
      flat.push({ ...cat, depth });
      if (cat.children?.length && (expanded.has(cat.id_category) || search)) {
        flat.push(...flatCategories(cat.children, depth + 1));
      }
    }
    return flat;
  };

  const flatList = flatCategories(categories);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/categories/export-csv`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'categories.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Chyba exportu'); }
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/admin/categories/import-csv`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      toast.success(`Import dokoncen: ${res.data.created} vytvoreno, ${res.data.updated} aktualizovano`);
      loadCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba importu'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  return (
    <AdminLayout active="categories">
      <div className="flex items-center justify-between mb-4" data-testid="admin-categories">
        <h1 className="text-2xl font-bold text-slate-800">Kategorie</h1>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} className="h-10 px-3 border border-slate-300 text-slate-600 rounded text-sm hover:bg-slate-50 flex items-center gap-1" data-testid="export-categories-csv"><Download className="w-4 h-4" /> CSV</button>
          <label className={`h-10 px-3 border border-slate-300 text-slate-600 rounded text-sm hover:bg-slate-50 flex items-center gap-1 cursor-pointer ${importing ? 'opacity-50' : ''}`} data-testid="import-categories-csv">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
            <input type="file" accept=".csv" onChange={importCSV} className="hidden" />
          </label>
          <button onClick={() => navigate('/admin/kategorie/nova')} className="h-10 px-4 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73] transition-colors" data-testid="add-category-btn">
            <Plus className="w-4 h-4" /> Nova kategorie
          </button>
        </div>
      </div>

      {/* Search & tools */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Hledat kategorii..."
            className="w-full h-10 pl-10 pr-4 border border-slate-300 rounded text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none" data-testid="search-categories" />
        </div>
        <button onClick={expandAll} className="h-10 px-3 border border-slate-300 text-slate-600 rounded text-sm hover:bg-slate-50">Rozbalit vse</button>
        <button onClick={() => setExpanded(new Set())} className="h-10 px-3 border border-slate-300 text-slate-600 rounded text-sm hover:bg-slate-50">Sbalit vse</button>
      </div>

      {/* Bulk toolbar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="category-bulk-toolbar">
          <span className="text-sm font-medium text-blue-700">Vybrano: {selected.size}</span>
          <button onClick={bulkDelete} disabled={bulkProcessing} className="h-9 px-4 bg-red-600 text-white rounded text-sm font-medium disabled:opacity-50 flex items-center gap-2 hover:bg-red-700" data-testid="bulk-delete-categories-btn">
            {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Smazat vybrane
          </button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-slate-500 hover:text-slate-700">Zrusit vyber</button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div> : flatList.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Zadne kategorie</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-10"><input type="checkbox" checked={flatList.length > 0 && selected.size === flatList.length} onChange={() => selected.size === flatList.length ? setSelected(new Set()) : setSelected(new Set(flatList.map(c => c.id_category)))} className="rounded border-slate-300" /></th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">ID</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Nazev</th>
                <th className="text-center px-3 py-3 text-xs font-bold uppercase text-slate-500">Stav</th>
                <th className="text-center px-3 py-3 text-xs font-bold uppercase text-slate-500">Menu</th>
                <th className="text-center px-3 py-3 text-xs font-bold uppercase text-slate-500">Pozice</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Akce</th>
              </tr>
            </thead>
            <tbody>
              {flatList.map((cat, idx) => (
                <tr key={cat.id_category} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(cat.id_category) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-3"><input type="checkbox" checked={selected.has(cat.id_category)} onChange={() => toggleSelect(cat.id_category)} className="rounded border-slate-300" data-testid={`select-cat-${cat.id_category}`} /></td>
                  <td className="px-4 py-3 text-sm text-slate-400 font-mono">{cat.id_category}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center" style={{ paddingLeft: `${cat.depth * 24}px` }}>
                      {cat.children?.length > 0 ? (
                        <button onClick={() => toggleExpand(cat.id_category)} className="p-1 mr-1 text-slate-400 hover:text-slate-600">
                          {expanded.has(cat.id_category) || search ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      ) : <span className="w-6 mr-1" />}
                      <FolderTree className="w-4 h-4 text-slate-400 mr-2 flex-shrink-0" />
                      <button onClick={() => navigate(`/admin/kategorie/${cat.id_category}`)} className="text-sm font-medium text-slate-800 hover:text-[#004E98] text-left" data-testid={`cat-name-${cat.id_category}`}>
                        {cat.name}
                      </button>
                      {cat.children?.length > 0 && <span className="text-xs text-slate-400 ml-2">({cat.children.length})</span>}
                      {cat.icon && <span className="text-xs text-slate-400 ml-2 bg-slate-100 px-1.5 py-0.5 rounded">{cat.icon}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => toggleActive(cat.id_category, cat.active)} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${cat.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-testid={`toggle-active-${cat.id_category}`}>
                      {cat.active ? <Eye className="w-3 h-3 inline mr-1" /> : <EyeOff className="w-3 h-3 inline mr-1" />}{cat.active ? 'Ano' : 'Ne'}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => toggleMenu(cat.id_category, cat.show_in_menu)} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${cat.show_in_menu ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`} data-testid={`toggle-menu-${cat.id_category}`}>
                      <Menu className="w-3 h-3 inline mr-1" />{cat.show_in_menu ? 'Ano' : 'Ne'}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-slate-400">{cat.position || 0}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => navigate(`/admin/kategorie/${cat.id_category}`)} className="p-1.5 text-[#004E98] hover:bg-blue-50 rounded" title="Upravit" data-testid={`edit-cat-${cat.id_category}`}><Edit className="w-4 h-4" /></button>
                      <button onClick={() => deleteCategory(cat.id_category)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Smazat" data-testid={`delete-cat-${cat.id_category}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
